/**
 * Cleaning Standards Drawer V2 - Process-Based with Original Styles
 * Maintains the original visual design while implementing Process-Based logic
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Check,
  Info,
  Trash2,
  Pencil,
  Clock,
  XCircle,
  AlertTriangle,
  Bot,
  Sparkles,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { CleaningWindowCard } from './CleaningWindowCardV2';
import { SaveFirstBanner } from './SaveFirstBanner';
import { SessionScheduleSimple } from './SessionScheduleSimple';
import { TimeStandardControl } from './TimeStandardControl';
import { AddPlaceholder } from '../common/AddPlaceHolderV2';
import { CopyFromStandardsButton } from './CopyFromStandardsButtonV2';
import { validateProcessConfiguration, calculateMinutes } from '../../utils/cleaningValidation';
import { createDefaultSession } from '../../utils/sessionMigrations';
import {
  buildLocationStandardsPayload,
  buildLocationStandardsDraftPayload,
  apiStandardsToDrawerState,
} from '../../utils/locationCleaningStandardsMapper';
import { useFeedbackContext } from '../../context';
import {
  SECTION_ORDER,
  SECTION_NAMES,
  DAYS,
  DEVICE_PROCESS_MAP,
  DEFAULT_WINDOW_ID,
  createInitialWindow,
} from './constants/cleaningStandardsDrawerConstants';
import {
  INFO_CARD_WRAPPER_P5,
  INFO_CARD_TITLE,
  INFO_CARD_TEXT,
} from './constants/infoCardStyles';
import { serviceLogger } from '../../utils/serviceLogger';

export function CleaningStandardsDrawer({
  isOpen,
  onClose,
  locations,
  onSave,
  onSaveDraft,
  allLocations,
  onCopyFrom,
  onCopyToLocations,
  copyResult,
  initialStandards,
  onInitialStandardsApplied,
}) {
  const { showToast } = useFeedbackContext();
  const [expandedSections, setExpandedSections] = useState(['cleaning-window']);
  const [showAllLocations, setShowAllLocations] = useState(false);

  const [isAddingDevice, setIsAddingDevice] = useState(false);
  const [addingDeviceToWindowId, setAddingDeviceToWindowId] = useState(null);
  const [newDeviceType, setNewDeviceType] = useState('');
  const [newDeviceTimeMin, setNewDeviceTimeMin] = useState('');
  const [newDeviceTimeMax, setNewDeviceTimeMax] = useState('');
  const [timeValidationError, setTimeValidationError] = useState('');

  const [ms50RinseWringCycles, setMs50RinseWringCycles] = useState('');
  const [editingProcessId, setEditingProcessId] = useState(null);

  const [windowMode, setWindowMode] = useState('same-time');
  const [frequency, setFrequency] = useState('3-per-week');

  const [windows, setWindows] = useState([createInitialWindow()]);

  // Other Cleaning Tasks state
  const [otherTasksMinDuration, setOtherTasksMinDuration] = useState('');
  const [otherTasksMaxDuration, setOtherTasksMaxDuration] = useState('');
  const [otherTasksTimeError, setOtherTasksTimeError] = useState('');

  const [selectedWindowId, setSelectedWindowId] = useState(DEFAULT_WINDOW_ID);
  const [expandedWindowIds, setExpandedWindowIds] = useState([]);

  const [sectionStates, setSectionStates] = useState({
    'cleaning-window': { status: 'not-configured' },
    'cleaning-processes': { status: 'not-configured' },
    'other-tasks': { status: 'not-configured' },
  });

  const [config, setConfig] = useState({
    locationId: locations[0]?.id || '',
    cleaningWindow: {
      frequency: '3x',
      selectedDays: [],
      timeWindow: {
        from: '08:00',
        to: '16:00',
      },
    },
    cleaningProcesses: [],
    otherCleaningTasks: [],
  });

  // Hydrate drawer from API standards (e.g. after Copy Apply) so user can review and Save
  useEffect(() => {
    if (!isOpen || !initialStandards) return;
    const state = apiStandardsToDrawerState(initialStandards);
    setWindows(state.windows);
    setOtherTasksMinDuration(state.otherTasksMinDuration);
    setOtherTasksMaxDuration(state.otherTasksMaxDuration);
    setWindowMode(state.windows.length > 1 ? 'different-times' : 'same-time');
    setSectionStates({
      'cleaning-window': { status: 'saved' },
      'cleaning-processes': { status: 'saved' },
      'other-tasks': { status: 'saved' },
    });
    setExpandedSections(['cleaning-window', 'cleaning-processes', 'other-tasks']);
    onInitialStandardsApplied?.();
  }, [initialStandards, isOpen, onInitialStandardsApplied]);

  // Reset state only when drawer opens (transition closed → open). Avoid resetting when
  // parent re-renders with a new `locations` reference while drawer is already open (would clear selected days).
  // When opening with initialStandards (e.g. after copy), skip reset so hydration keeps the copied days visible.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    if (!justOpened) return;
    if (initialStandards) return; // Let hydration effect own state; don't overwrite windows/days

    setSectionStates({
      'cleaning-window': { status: 'not-configured' },
      'cleaning-processes': { status: 'not-configured' },
      'other-tasks': { status: 'not-configured' },
    });

    setExpandedSections(['cleaning-window']);

    setWindows([createInitialWindow()]);
    setSelectedWindowId(DEFAULT_WINDOW_ID);
    setExpandedWindowIds([]);

    setOtherTasksMinDuration('');
    setOtherTasksMaxDuration('');
    setOtherTasksTimeError('');

    setIsAddingDevice(false);
    setAddingDeviceToWindowId(null);
    setNewDeviceType('');
    setNewDeviceTimeMin('');
    setNewDeviceTimeMax('');
    setTimeValidationError('');
    setMs50RinseWringCycles('');

    setConfig({
      locationId: locations[0]?.id || '',
      cleaningWindow: {
        frequency: '3x',
        selectedDays: [],
        timeWindow: {
          from: '08:00',
          to: '16:00',
        },
      },
      cleaningProcesses: [],
      otherCleaningTasks: [],
    });
  }, [isOpen, locations, initialStandards]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Auto-select first configured window when windows change
  useEffect(() => {
    const configuredWindows = windows.filter((w) => w.days.length > 0);
    if (configuredWindows.length === 0) return;

    const currentWindow = windows.find((w) => w.id === selectedWindowId);
    if (!currentWindow || currentWindow.days.length === 0) {
      setSelectedWindowId(configuredWindows[0].id);
    }

    if (
      configuredWindows.length > 1 &&
      expandedSections.includes('cleaning-processes') &&
      expandedWindowIds.length === 0
    ) {
      setExpandedWindowIds([configuredWindows[0].id]);
    }
  }, [windows, selectedWindowId, expandedSections, expandedWindowIds.length]);

  const toggleSection = (section) => {
    setExpandedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section],
    );
  };

  const savedSectionsCount = useMemo(
    () => Object.values(sectionStates).filter((s) => s.status === 'saved').length,
    [sectionStates],
  );

  const totalSections = SECTION_ORDER.length;

  const allWindowsHaveDevices = useMemo(
    () =>
      windows.filter((w) => w.days.length > 0).every((w) => (w.cleaningProcesses || []).length > 0),
    [windows],
  );

  const canSave = savedSectionsCount >= 2 && allWindowsHaveDevices;

  // Validate Other Tasks time
  const validateOtherTasksTime = (min, max) => {
    if (!min && !max) {
      setOtherTasksTimeError('');
      return true;
    }

    if (!min || !max) {
      setOtherTasksTimeError('Both minimum and maximum are required');
      return false;
    }

    const minNum = parseInt(min, 10);
    const maxNum = parseInt(max, 10);

    if (Number.isNaN(minNum) || Number.isNaN(maxNum)) {
      setOtherTasksTimeError('Please enter valid numbers');
      return false;
    }

    if (minNum <= 0 || maxNum <= 0) {
      setOtherTasksTimeError('Time must be greater than 0');
      return false;
    }

    if (minNum > maxNum) {
      setOtherTasksTimeError('Maximum must be greater than or equal to minimum');
      return false;
    }

    setOtherTasksTimeError('');
    return true;
  };

  const handleSaveSection = (section) => {
    if (section === 'cleaning-window') {
      const daysOk =
        windowMode === 'same-time'
          ? windows[0].days.length === maxAllowedDays2
          : totalAssignedDays === maxAllowedDays2 && remainingDays === 0;
      if (!daysOk) {
        showToast({
          variant: 'error',
          title: 'Error',
          copy: `Please select ${maxAllowedDays2} ${maxAllowedDays2 === 1 ? 'day' : 'days'}`,
          duration: 4000,
        });
        return;
      }
    }

    if (section === 'other-tasks') {
      if (
        (otherTasksMinDuration && !otherTasksMaxDuration) ||
        (!otherTasksMinDuration && otherTasksMaxDuration)
      ) {
        showToast({
          variant: 'error',
          title: 'Error',
          copy: 'Both minimum and maximum duration are required',
          duration: 4000,
        });
        return;
      }

      if (otherTasksMinDuration && otherTasksMaxDuration) {
        if (!validateOtherTasksTime(otherTasksMinDuration, otherTasksMaxDuration)) {
          showToast({
            variant: 'error',
            title: 'Error',
            copy: 'Please fix validation errors',
            duration: 4000,
          });
          return;
        }
      }
    }

    setSectionStates((prev) => ({
      ...prev,
      [section]: { status: 'saved' },
    }));

    showToast({
      variant: 'success',
      title: 'Success',
      copy: `${SECTION_NAMES[section]} saved successfully`,
      duration: 3000,
    });

    const currentIndex = SECTION_ORDER.indexOf(section);
    const nextSection = SECTION_ORDER[currentIndex + 1];
    if (nextSection) {
      setExpandedSections([nextSection]);
    } else {
      // Last section (e.g. Other Cleaning Tasks): close accordion so user sees config is complete
      setExpandedSections((prev) => prev.filter((s) => s !== section));
    }
  };

  const handleFinalSave = async () => {
    if (!canSave) {
      if (!allWindowsHaveDevices)
        showToast({
          variant: 'error',
          title: 'Error',
          copy: 'Each configured window must have at least one cleaning device',
          duration: 4000,
        });
      else
        showToast({
          variant: 'error',
          title: 'Error',
          copy: 'Please configure Cleaning Window and Cleaning Processes',
          duration: 4000,
        });
      return;
    }

    try {
      const apiPayload = buildLocationStandardsPayload(
        windows,
        otherTasksMinDuration,
        otherTasksMaxDuration,
      );
      await onSave?.(apiPayload);
      // Success toast and drawer close are handled by parent (e.g. LocationsPanel)
      onClose();
    } catch (error) {
      showToast({
        variant: 'error',
        title: 'Error',
        copy: error?.message || 'Failed to save cleaning standards',
        duration: 4000,
      });
      serviceLogger.error(error);
    }
  };

  const handleSaveProgress = async () => {
    try {
      const draftPayload = buildLocationStandardsDraftPayload(
        windows,
        otherTasksMinDuration,
        otherTasksMaxDuration,
      );
      await onSaveDraft?.(draftPayload);
      // Success toast is shown by parent (e.g. LocationsPanel) to avoid duplicate and to show location count
      onClose();
    } catch (error) {
      showToast({
        variant: 'error',
        title: 'Error',
        copy: error?.message || 'Failed to save progress',
        duration: 4000,
      });
    }
  };

  const renderStatusIcon = (section) => {
    const state = sectionStates[section];
    if (state.status !== 'saved') return null;

    return (
      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
        <CheckCircle2 className="w-4 h-4 text-green-600" />
      </div>
    );
  };

  // Frequency mapping (legacy config)
  const getMaxAllowedDays = (freq) => {
    switch (freq) {
      case '1-per-week':
        return 1;
      case '2-per-week':
        return 2;
      case '3-per-week':
        return 3;
      case '4-per-week':
        return 4;
      case '5-per-week':
        return 5;
      case '6-per-week':
        return 6;
      case '7-per-week':
        return 7;
      case 'daily':
        return 7;
      default:
        return 7;
    }
  };

  const maxAllowedDays2 = getMaxAllowedDays(frequency);

  const totalAssignedDays = windows.reduce((sum, window) => sum + window.days.length, 0);
  const remainingDays = maxAllowedDays2 - totalAssignedDays;

  const emptyWindowsCount =
    windowMode === 'different-times' ? windows.filter((w) => w.days.length === 0).length : 0;

  const getUsedDays = (currentWindowId) => {
    return windows.filter((w) => w.id !== currentWindowId).flatMap((w) => w.days);
  };

  const getAvailableDays = (currentWindowId) => {
    const usedDays = getUsedDays(currentWindowId);
    return DAYS.map((d) => d.full).filter((day) => !usedDays.includes(day));
  };

  const addWindow = () => {
    const newId = `window-${windows.length + 1}`;
    setWindows((prev) => [
      ...prev,
      {
        id: newId,
        name: `Window ${prev.length + 1}`,
        days: [],
        sessionType: 'single',
        sessions: [createDefaultSession()],
        cleaningProcesses: [],
      },
    ]);

    if (sectionStates['cleaning-window'].status === 'saved') {
      setSectionStates((prev) => ({ ...prev, 'cleaning-window': { status: 'not-configured' } }));
    }

    showToast({
      variant: 'success',
      title: 'Success',
      copy: 'New window added',
      duration: 3000,
    });
  };

  const updateWindow = (windowId, updatedWindow) => {
    setWindows((prev) => prev.map((w) => (w.id === windowId ? updatedWindow : w)));

    if (sectionStates['cleaning-window'].status === 'saved') {
      setSectionStates((prev) => ({ ...prev, 'cleaning-window': { status: 'not-configured' } }));
    }
  };

  const removeWindow = (windowId) => {
    setWindows((prev) => prev.filter((w) => w.id !== windowId));

    if (sectionStates['cleaning-window'].status === 'saved') {
      setSectionStates((prev) => ({ ...prev, 'cleaning-window': { status: 'not-configured' } }));
    }

    showToast({
      variant: 'success',
      title: 'Success',
      copy: 'Window removed',
      duration: 3000,
    });
  };


  const handleStartAddingDevice = (windowId) => {
    setIsAddingDevice(true);
    setAddingDeviceToWindowId(windowId || selectedWindowId);
    setNewDeviceType('');
    setNewDeviceTimeMin('');
    setNewDeviceTimeMax('');
    setTimeValidationError('');
    setMs50RinseWringCycles('');
  };

  const handleCancelAddingDevice = () => {
    setIsAddingDevice(false);
    setAddingDeviceToWindowId(null);
    setEditingProcessId(null);
    setNewDeviceType('');
    setNewDeviceTimeMin('');
    setNewDeviceTimeMax('');
    setTimeValidationError('');
    setMs50RinseWringCycles('');
  };

  const handleStartEditingProcess = (process, targetWindowId) => {
    setEditingProcessId(process.id);
    setAddingDeviceToWindowId(targetWindowId);
    setIsAddingDevice(true);
    setNewDeviceType(process.assignedDevice);
    setNewDeviceTimeMin(String(process.timeStandard.minimum));
    setNewDeviceTimeMax(String(process.timeStandard.maximum));
    setTimeValidationError('');
    setMs50RinseWringCycles(
      process.deviceConfiguration?.rinseWringCycles != null
        ? String(process.deviceConfiguration.rinseWringCycles)
        : '',
    );
  };

  const validateTimeInputs = (min, max) => {
    if (!min || !max) {
      setTimeValidationError('');
      return false;
    }

    const minNum = parseInt(min, 10);
    const maxNum = parseInt(max, 10);

    if (Number.isNaN(minNum) || Number.isNaN(maxNum)) {
      setTimeValidationError('Please enter valid numbers');
      return false;
    }

    if (minNum <= 0 || maxNum <= 0) {
      setTimeValidationError('Time must be greater than 0');
      return false;
    }

    if (minNum > maxNum) {
      setTimeValidationError('Maximum must be greater than or equal to minimum');
      return false;
    }

    setTimeValidationError('');
    return true;
  };

  const handleTimeMinChange = (value) => {
    setNewDeviceTimeMin(value);
    validateTimeInputs(value, newDeviceTimeMax);
  };

  const handleTimeMaxChange = (value) => {
    setNewDeviceTimeMax(value);
    validateTimeInputs(newDeviceTimeMin, value);
  };

  const handleOtherTasksMinChange = (value) => {
    setOtherTasksMinDuration(value);
    validateOtherTasksTime(value, otherTasksMaxDuration);
  };

  const handleOtherTasksMaxChange = (value) => {
    setOtherTasksMaxDuration(value);
    validateOtherTasksTime(otherTasksMinDuration, value);
  };

  const handleSaveNewDevice = () => {
    if (!newDeviceType || !newDeviceTimeMin || !newDeviceTimeMax) {
      showToast({
        variant: 'error',
        title: 'Error',
        copy: 'Please fill all required fields',
        duration: 4000,
      });
      return;
    }

    if (!validateTimeInputs(newDeviceTimeMin, newDeviceTimeMax)) {
      showToast({
        variant: 'error',
        title: 'Error',
        copy: 'Please fix validation errors',
        duration: 4000,
      });
      return;
    }

    const timeMin = parseInt(newDeviceTimeMin, 10);
    const timeMax = parseInt(newDeviceTimeMax, 10);

    if (newDeviceType === 'MS50') {
      if (!ms50RinseWringCycles || parseInt(ms50RinseWringCycles, 10) < 1) {
        showToast({
          variant: 'error',
          title: 'Error',
          copy: 'MS50 requires Rinse & Wring Cycles configuration',
          duration: 4000,
        });
        return;
      }
    }

    const processPayload = {
      processType: DEVICE_PROCESS_MAP[newDeviceType],
      assignedDevice: newDeviceType,
      timeStandard: {
        minimum: timeMin,
        maximum: timeMax,
      },
    };

    if (newDeviceType === 'MS50') {
      processPayload.deviceConfiguration = {
        deviceType: 'MS50',
        moppingDuration: { minimum: timeMin, maximum: timeMax },
        rinseWringCycles: parseInt(ms50RinseWringCycles, 10),
      };
    }

    const targetWindowId = addingDeviceToWindowId || selectedWindowId;
    const isEditing = editingProcessId != null;
    const processToSave = {
      ...processPayload,
      id: isEditing ? editingProcessId : `process-${Date.now()}`,
    };

    if (isEditing) {
      setWindows((prev) =>
        prev.map((w) => {
          if (w.id !== targetWindowId) return w;
          return {
            ...w,
            cleaningProcesses: (w.cleaningProcesses || []).map((p) =>
              p.id === editingProcessId ? processToSave : p,
            ),
          };
        }),
      );
      setConfig((prev) => ({
        ...prev,
        cleaningProcesses: prev.cleaningProcesses.map((p) =>
          p.id === editingProcessId ? processToSave : p,
        ),
      }));
    } else {
      setWindows((prev) =>
        prev.map((w) => {
          if (w.id !== targetWindowId) return w;
          return { ...w, cleaningProcesses: [...(w.cleaningProcesses || []), processToSave] };
        }),
      );
      setConfig((prev) => ({
        ...prev,
        cleaningProcesses: [...prev.cleaningProcesses, processToSave],
      }));
    }

    if (sectionStates['cleaning-processes'].status === 'saved') {
      setSectionStates((prev) => ({ ...prev, 'cleaning-processes': { status: 'not-configured' } }));
    }

    handleCancelAddingDevice();
    showToast({
      variant: 'success',
      title: 'Success',
      copy: isEditing ? `${newDeviceType} updated` : `${newDeviceType} added`,
      duration: 3000,
    });
  };

  const handleDeleteProcess = (processId) => {
    if (processId === editingProcessId) {
      handleCancelAddingDevice();
    }

    setWindows((prev) =>
      prev.map((w) =>
        w.id === selectedWindowId
          ? {
              ...w,
              cleaningProcesses: (w.cleaningProcesses || []).filter((p) => p.id !== processId),
            }
          : w,
      ),
    );

    setConfig((prev) => ({
      ...prev,
      cleaningProcesses: prev.cleaningProcesses.filter((p) => p.id !== processId),
    }));

    if (sectionStates['cleaning-processes'].status === 'saved') {
      setSectionStates((prev) => ({ ...prev, 'cleaning-processes': { status: 'not-configured' } }));
    }
  };

  const toggleWindowAccordion = (windowId) => {
    setExpandedWindowIds((prev) =>
      prev.includes(windowId) ? prev.filter((id) => id !== windowId) : [...prev, windowId],
    );
  };

  const selectedWindow = windows.find((w) => w.id === selectedWindowId) || windows[0];

  const currentProcesses = useMemo(() => {
    const baseWindow = windowMode === 'same-time' ? windows[0] : selectedWindow;
    return baseWindow?.cleaningProcesses || [];
  }, [windowMode, windows, selectedWindow]);

  const totalMinutes = useMemo(() => {
    const session = selectedWindow?.sessions?.[0];
    const from = session?.primaryRange?.from || config.cleaningWindow.timeWindow.from;
    const to = session?.primaryRange?.to || config.cleaningWindow.timeWindow.to;
    return calculateMinutes(from, to);
  }, [selectedWindow, config.cleaningWindow.timeWindow.from, config.cleaningWindow.timeWindow.to]);

  const validation = useMemo(
    () =>
      validateProcessConfiguration(
        currentProcesses,
        totalMinutes,
        otherTasksMinDuration ? parseInt(otherTasksMinDuration, 10) : undefined,
        otherTasksMaxDuration ? parseInt(otherTasksMaxDuration, 10) : undefined,
      ),
    [currentProcesses, totalMinutes, otherTasksMinDuration, otherTasksMaxDuration],
  );

  const devicesTimeUsed = currentProcesses.reduce(
    (sum, process) => sum + process.timeStandard.maximum,
    0,
  );
  const otherTasksTimeUsed = otherTasksMaxDuration ? parseInt(otherTasksMaxDuration, 10) : 0;
  const totalTimeUsed = devicesTimeUsed + otherTasksTimeUsed;

  const timeRemaining = totalMinutes - totalTimeUsed;

  const alreadyAddedDevices = currentProcesses.map((p) => p.assignedDevice);
  const canAddMoreDevices = alreadyAddedDevices.length < 3 && timeRemaining > 0;

  const processesInCurrentFormWindow = useMemo(() => {
    const windowId = addingDeviceToWindowId || selectedWindowId;
    return windows.find((w) => w.id === windowId)?.cleaningProcesses || [];
  }, [windows, addingDeviceToWindowId, selectedWindowId]);

  const alreadyAddedDevicesForSelect = useMemo(() => {
    const devices = processesInCurrentFormWindow.map((p) => p.assignedDevice);
    if (editingProcessId) {
      const editingProcess = processesInCurrentFormWindow.find((p) => p.id === editingProcessId);
      if (editingProcess) {
        return devices.filter((d) => d !== editingProcess.assignedDevice);
      }
    }
    return devices;
  }, [processesInCurrentFormWindow, editingProcessId]);

  const isCleaningWindowSaved = sectionStates['cleaning-window'].status === 'saved';

  const hasMultipleWindows =
    windowMode === 'different-times' && windows.filter((w) => w.days.length > 0).length > 1;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/50 z-[100]"
              onClick={onClose}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            />

            {/* Drawer */}
            <motion.div
              className="fixed top-0 right-0 h-full w-full max-w-[680px] bg-white z-[110] shadow-2xl"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200, mass: 0.8 }}
            >
              <div className="h-full flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-8 py-6 border-b border-gray-200">
                  <div>
                    <h2 className="font-['Roboto:Bold',sans-serif] font-bold text-[24px] text-black">
                      Set Cleaning Standards
                    </h2>
                    <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-500 mt-1">
                      {savedSectionsCount} of {totalSections} sections configured
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                    type="button"
                  >
                    <X className="size-5 text-gray-600" />
                  </button>
                </div>

                {/* Selected Locations Preview */}
                <div className="bg-[#f8fbfd] border-b border-[#e5e5e5] px-8 py-4">
                  <p className="font-['Roboto:Medium',sans-serif] text-[13px] text-gray-600 mb-2 uppercase tracking-wide">
                    Selected Areas:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(showAllLocations ? locations : locations.slice(0, 5)).map((location, locIndex) => (
                      <div
                        key={location?.id ?? `location-${locIndex}`}
                        className="bg-white border border-gray-300 rounded-full px-3 py-1.5 flex items-center gap-2"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#61b8e7]" strokeWidth={2.5} />
                        <span className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-800">
                          {location.name}
                        </span>
                      </div>
                    ))}

                    {locations.length > 5 && !showAllLocations && (
                      <button
                        onClick={() => setShowAllLocations(true)}
                        className="bg-[#61b8e7]/10 border border-[#61b8e7]/30 rounded-full px-3 py-1.5"
                        type="button"
                      >
                        <span className="font-['Roboto:Medium',sans-serif] text-[13px] text-[#61b8e7]">
                          +{locations.length - 5} more
                        </span>
                      </button>
                    )}

                    {locations.length > 5 && showAllLocations && (
                      <button
                        onClick={() => setShowAllLocations(false)}
                        className="bg-gray-100 border border-gray-300 rounded-full px-3 py-1.5"
                        type="button"
                      >
                        <span className="font-['Roboto:Medium',sans-serif] text-[13px] text-gray-600">
                          View less
                        </span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Copy FROM: show only when (1) there is another area with standards to copy from, (2) current area(s) do NOT already have standards configured */}
                {(() => {
                  const currentLocationIds = new Set(
                    (locations?.map((l) => String(l?.id)) ?? []).filter(Boolean),
                  );
                  const hasOtherLocationWithStandards =
                    Array.isArray(allLocations) &&
                    allLocations.some(
                      (loc) =>
                        loc?.hasStandards &&
                        loc?.id != null &&
                        !currentLocationIds.has(String(loc.id)),
                    );
                  const currentLocationsAllHaveStandards =
                    (locations?.length ?? 0) > 0 &&
                    (locations ?? []).every((l) => {
                      const found = allLocations?.find(
                        (loc) => loc?.id != null && String(loc.id) === String(l?.id),
                      );
                      return found?.hasStandards === true;
                    });
                  const showCopyFrom =
                    hasOtherLocationWithStandards &&
                    !currentLocationsAllHaveStandards &&
                    onCopyFrom;
                  return showCopyFrom ? (
                    <CopyFromStandardsButton onCopyFrom={onCopyFrom} />
                  ) : null;
                })()}

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-8 py-6">
                  <div className="space-y-4">
                    {/* 1. Cleaning Window */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleSection('cleaning-window')}
                        className="w-full bg-white hover:bg-gray-50 px-5 py-4 flex items-center justify-between transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {renderStatusIcon('cleaning-window')}
                          <h3 className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[16px] text-black">
                            Cleaning Window
                          </h3>
                        </div>
                        {expandedSections.includes('cleaning-window') ? (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-500" />
                        )}
                      </button>

                      <AnimatePresence>
                        {expandedSections.includes('cleaning-window') && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="bg-gray-50 px-5 py-5 space-y-5">
                              {/* Cleaning Frequency */}
                              <div>
                                <label className="block font-['Roboto:Medium',sans-serif] text-[14px] text-gray-700 mb-2">
                                  How often should cleaning happen?
                                </label>
                                <select
                                  value={frequency}
                                  onChange={(e) => {
                                    const newFreq = e.target.value;
                                    setFrequency(newFreq);

                                    setWindows([createInitialWindow()]);
                                    if (sectionStates['cleaning-window'].status === 'saved') {
                                      setSectionStates((prev) => ({
                                        ...prev,
                                        'cleaning-window': { status: 'not-configured' },
                                      }));
                                    }
                                  }}
                                  className="w-full min-h-[44px] border-2 border-gray-300 rounded-lg pl-4 pr-10 py-2.5 font-['Roboto:Regular',sans-serif] text-[14px] text-left leading-normal focus:border-[#61b8e7] focus:outline-none appearance-none bg-no-repeat bg-[length:16px_16px]"
                                  style={{
                                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
                                    backgroundPosition: 'right 12px center',
                                  }}
                                >
                                  <option value="1-per-week">1 time per week</option>
                                  <option value="2-per-week">2 times per week</option>
                                  <option value="3-per-week">3 times per week</option>
                                  <option value="4-per-week">4 times per week</option>
                                  <option value="5-per-week">5 times per week</option>
                                  <option value="6-per-week">6 times per week</option>
                                  <option value="7-per-week">7 times per week</option>
                                </select>
                              </div>

                              {/* Same vs Different Times */}
                              <div>
                                <div className="flex items-center gap-2 mb-3">
                                  <label className="font-['Roboto:Medium',sans-serif] text-[14px] text-gray-700">
                                    Do all days have the same cleaning window?
                                  </label>
                                  <div className="group relative">
                                    <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                                    <div className="invisible group-hover:visible absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 z-50">
                                      A "window" is a time slot when cleaning must be performed.
                                      Different days can have different windows.
                                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                  </div>
                                </div>

                                <div className="space-y-3">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setWindowMode('same-time');

                                      setWindows([
                                        {
                                          ...createInitialWindow(),
                                          days: windows
                                            .flatMap((w) => w.days)
                                            .slice(0, maxAllowedDays2),
                                          cleaningProcesses: windows[0]?.cleaningProcesses || [],
                                        },
                                      ]);

                                      if (sectionStates['cleaning-window'].status === 'saved') {
                                        setSectionStates((prev) => ({
                                          ...prev,
                                          'cleaning-window': { status: 'not-configured' },
                                        }));
                                      }
                                    }}
                                    className={`
                                      w-full text-left p-4 rounded-lg border-2 transition-all
                                      ${
                                        windowMode === 'same-time'
                                          ? 'border-[#61b8e7] bg-[#f0f9ff]'
                                          : 'border-gray-300 bg-white hover:border-gray-400'
                                      }
                                    `}
                                  >
                                    <div className="flex items-start gap-3">
                                      <div
                                        className={`
                                        w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0
                                        ${windowMode === 'same-time' ? 'border-[#61b8e7] bg-[#61b8e7]' : 'border-gray-400'}
                                      `}
                                      >
                                        {windowMode === 'same-time' && (
                                          <div className="w-2 h-2 rounded-full bg-white"></div>
                                        )}
                                      </div>
                                      <div>
                                        <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[14px] text-gray-900">
                                          Yes - Same window for all days
                                        </p>
                                        <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-600 mt-1">
                                          All {maxAllowedDays2} days will have the same cleaning
                                          window
                                        </p>
                                      </div>
                                    </div>
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => {
                                      setWindowMode('different-times');

                                      if (windows.length === 1 && windows[0].days.length > 0) {
                                        // Keep current window
                                      } else {
                                        setWindows([createInitialWindow()]);
                                      }
                                      if (sectionStates['cleaning-window'].status === 'saved') {
                                        setSectionStates((prev) => ({
                                          ...prev,
                                          'cleaning-window': { status: 'not-configured' },
                                        }));
                                      }
                                    }}
                                    className={`
                                      w-full text-left p-4 rounded-lg border-2 transition-all
                                      ${
                                        windowMode === 'different-times'
                                          ? 'border-[#61b8e7] bg-[#f0f9ff]'
                                          : 'border-gray-300 bg-white hover:border-gray-400'
                                      }
                                    `}
                                  >
                                    <div className="flex items-start gap-3">
                                      <div
                                        className={`
                                        w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0
                                        ${
                                          windowMode === 'different-times'
                                            ? 'border-[#61b8e7] bg-[#61b8e7]'
                                            : 'border-gray-400'
                                        }
                                      `}
                                      >
                                        {windowMode === 'different-times' && (
                                          <div className="w-2 h-2 rounded-full bg-white"></div>
                                        )}
                                      </div>
                                      <div>
                                        <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[14px] text-gray-900">
                                          No - Different windows for different days
                                        </p>
                                        <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-600 mt-1">
                                          Create multiple windows with different schedules
                                        </p>
                                      </div>
                                    </div>
                                  </button>
                                </div>
                              </div>

                              {/* SAME TIME MODE */}
                              {windowMode === 'same-time' && (
                                <>
                                  {/* Select Days */}
                                  <div>
                                    <div className="flex items-center justify-between mb-2">
                                      <label className="font-['Roboto:Medium',sans-serif] text-[14px] text-gray-700">
                                        Select {maxAllowedDays2}{' '}
                                        {maxAllowedDays2 === 1 ? 'day' : 'days'}
                                      </label>
                                      <span
                                        className={`font-['Roboto:SemiBold',sans-serif] text-[13px] px-2.5 py-1 rounded-full ${
                                          windows[0].days.length > maxAllowedDays2
                                            ? 'bg-red-100 text-red-700'
                                            : windows[0].days.length === maxAllowedDays2
                                              ? 'bg-green-100 text-green-700'
                                              : windows[0].days.length === 0
                                                ? 'bg-red-100 text-red-700'
                                                : 'bg-blue-100 text-blue-700'
                                        }`}
                                      >
                                        {windows[0].days.length}/{maxAllowedDays2}
                                      </span>
                                    </div>

                                    <div className="flex gap-2 mb-2">
                                      {DAYS.map((day, dayIndex) => {
                                        const isSelected = windows[0].days.includes(day.full);
                                        const canSelect = windows[0].days.length < maxAllowedDays2;
                                        const isDisabled = !isSelected && !canSelect;
                                        const dayKey = `day-${day.full}-${dayIndex}`;

                                        return (
                                          <button
                                            key={dayKey}
                                            type="button"
                                            onClick={() => {
                                              if (!isDisabled || isSelected) {
                                                const newDays = isSelected
                                                  ? windows[0].days.filter((d) => d !== day.full)
                                                  : [...windows[0].days, day.full];

                                                setWindows([
                                                  {
                                                    ...windows[0],
                                                    days: newDays,
                                                  },
                                                ]);

                                                if (
                                                  sectionStates['cleaning-window'].status ===
                                                  'saved'
                                                ) {
                                                  setSectionStates((prev) => ({
                                                    ...prev,
                                                    'cleaning-window': { status: 'not-configured' },
                                                  }));
                                                }
                                              }
                                            }}
                                            disabled={isDisabled}
                                            className={`
                                              w-10 h-10 rounded-full font-['Roboto:Medium',sans-serif] text-[14px] transition-all
                                              ${
                                                isSelected
                                                  ? 'bg-[#61b8e7] text-white'
                                                  : isDisabled
                                                    ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                                                    : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400 cursor-pointer'
                                              }
                                            `}
                                          >
                                            {day.short}
                                          </button>
                                        );
                                      })}
                                    </div>

                                    {windows[0].days.length === 0 && (
                                      <p className="font-['Roboto:Regular',sans-serif] text-[12px] text-red-600">
                                        Please select {maxAllowedDays2}{' '}
                                        {maxAllowedDays2 === 1 ? 'day' : 'days'}
                                      </p>
                                    )}
                                    {windows[0].days.length < maxAllowedDays2 &&
                                      windows[0].days.length > 0 && (
                                        <p className="font-['Roboto:Regular',sans-serif] text-[12px] text-blue-600">
                                          Select {maxAllowedDays2 - windows[0].days.length} more{' '}
                                          {maxAllowedDays2 - windows[0].days.length === 1
                                            ? 'day'
                                            : 'days'}
                                        </p>
                                      )}
                                    {windows[0].days.length === maxAllowedDays2 && (
                                      <p className="font-['Roboto:Regular',sans-serif] text-[12px] text-green-600 flex items-center gap-1.5">
                                        <Check className="w-4 h-4 shrink-0" aria-hidden />
                                        All days selected
                                      </p>
                                    )}
                                  </div>

                                  {/* Session Schedule */}
                                  <SessionScheduleSimple
                                    sessionType={windows[0].sessionType}
                                    sessions={windows[0].sessions}
                                    daysCount={windows[0].days.length}
                                    onSessionTypeChange={(newType) => {
                                      setWindows([
                                        {
                                          ...windows[0],
                                          sessionType: newType,
                                          sessions:
                                            newType === 'single'
                                              ? [windows[0].sessions[0]]
                                              : windows[0].sessions,
                                        },
                                      ]);

                                      if (sectionStates['cleaning-window'].status === 'saved') {
                                        setSectionStates((prev) => ({
                                          ...prev,
                                          'cleaning-window': { status: 'not-configured' },
                                        }));
                                      }
                                    }}
                                    onSessionsUpdate={(newSessions) => {
                                      setWindows([{ ...windows[0], sessions: newSessions }]);

                                      if (sectionStates['cleaning-window'].status === 'saved') {
                                        setSectionStates((prev) => ({
                                          ...prev,
                                          'cleaning-window': { status: 'not-configured' },
                                        }));
                                      }
                                    }}
                                  />
                                </>
                              )}

                              {/* DIFFERENT TIMES MODE */}
                              {windowMode === 'different-times' && (
                                <>
                                  {/* Days Assignment Tracker - info card style when valid, red when error */}
                                  <div
                                    className={`rounded-2xl border p-4 ${
                                      totalAssignedDays > maxAllowedDays2
                                        ? 'bg-red-50 border-red-200'
                                        : 'border-[#A5D8FF] bg-[#E8F4FC]'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <p className={totalAssignedDays > maxAllowedDays2 ? 'font-["Roboto:SemiBold",sans-serif] font-semibold text-[14px] text-gray-900' : INFO_CARD_TITLE}>
                                          Days Assignment
                                        </p>
                                        <p className={totalAssignedDays > maxAllowedDays2 ? "font-['Roboto:Regular',sans-serif] text-[13px] text-gray-600 mt-0.5" : `${INFO_CARD_TEXT} mt-0.5`}>
                                          Assign all {maxAllowedDays2} days to windows
                                        </p>
                                      </div>
                                      <div className="text-right">
                                        <div
                                          className={`font-['Roboto:Bold',sans-serif] font-bold text-[20px] ${
                                            totalAssignedDays > maxAllowedDays2
                                              ? 'text-red-600'
                                              : totalAssignedDays === maxAllowedDays2
                                                ? 'text-green-600'
                                                : totalAssignedDays === 0
                                                  ? 'text-red-600'
                                                  : 'text-blue-600'
                                          }`}
                                        >
                                          {totalAssignedDays}/{maxAllowedDays2}
                                        </div>
                                        <p
                                          className={`font-['Roboto:Regular',sans-serif] text-[12px] ${
                                            totalAssignedDays > maxAllowedDays2
                                              ? 'text-red-600 font-semibold'
                                              : 'text-gray-600'
                                          }`}
                                        >
                                          {totalAssignedDays > maxAllowedDays2
                                            ? 'Error: Too many days!'
                                            : remainingDays > 0
                                              ? `${remainingDays} remaining`
                                              : 'Complete'}
                                        </p>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Window Cards */}
                                  <div className="space-y-3">
                                    {windows.map((window, winIndex) => (
                                      <CleaningWindowCard
                                        key={window?.id ?? `window-${winIndex}`}
                                        window={window}
                                        availableDays={getAvailableDays(window.id)}
                                        totalAssignedDays={totalAssignedDays}
                                        onUpdate={(updated) => updateWindow(window.id, updated)}
                                        onRemove={() => removeWindow(window.id)}
                                        canRemove={windows.length > 1}
                                        maxDaysAllowed={maxAllowedDays2}
                                        onChange={() => {
                                          if (sectionStates['cleaning-window'].status === 'saved') {
                                            setSectionStates((prev) => ({
                                              ...prev,
                                              'cleaning-window': { status: 'not-configured' },
                                            }));
                                          }
                                        }}
                                      />
                                    ))}
                                  </div>

                                  {remainingDays > 0 && (
                                    <AddPlaceholder
                                      label="Add Another Window"
                                      sublabel={`(${remainingDays} ${remainingDays === 1 ? 'day' : 'days'} remaining)`}
                                      onClick={addWindow}
                                    />
                                  )}

                                  {remainingDays === 0 && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                                      <CheckCircle2
                                        className="w-5 h-5 text-green-600"
                                        strokeWidth={2.5}
                                      />
                                      <p className="font-['Roboto:Medium',sans-serif] text-[13px] text-green-800">
                                        All days have been assigned to windows
                                      </p>
                                    </div>
                                  )}

                                  {emptyWindowsCount > 0 && windows.length > 1 && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                                      <Info
                                        className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
                                        strokeWidth={2}
                                      />
                                      <div className="flex-1">
                                        <p className="font-['Roboto:Medium',sans-serif] text-[13px] text-amber-900">
                                          {emptyWindowsCount === 1
                                            ? '1 empty window will be removed automatically when saved'
                                            : `${emptyWindowsCount} empty windows will be removed automatically when saved`}
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}

                              {/* Save Button */}
                              <button
                                type="button"
                                onClick={() => handleSaveSection('cleaning-window')}
                                disabled={
                                  windowMode === 'same-time'
                                    ? windows[0].days.length !== maxAllowedDays2
                                    : remainingDays > 0 || totalAssignedDays !== maxAllowedDays2
                                }
                                className="w-full bg-[#61b8e7] text-white rounded-lg px-6 py-3 font-['Roboto:Medium',sans-serif] text-[14px] hover:bg-[#4fa8d5] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                Save
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* 2. Cleaning Processes */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleSection('cleaning-processes')}
                        className="w-full bg-white hover:bg-gray-50 px-5 py-4 flex items-center justify-between transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {renderStatusIcon('cleaning-processes')}
                          <h3 className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[16px] text-black">
                            Cleaning Processes
                          </h3>
                        </div>
                        {expandedSections.includes('cleaning-processes') ? (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-500" />
                        )}
                      </button>

                      <AnimatePresence>
                        {expandedSections.includes('cleaning-processes') && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="bg-gray-50 px-5 py-5 space-y-5">
                              <div>
                                <h4 className="font-['Roboto:SemiBold',sans-serif] text-[18px] text-black mb-2">
                                  Cleaning Devices
                                </h4>
                                <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-600">
                                  {hasMultipleWindows
                                    ? 'Configure devices for each cleaning window below'
                                    : 'Add devices and set time standards for each'}
                                </p>
                              </div>

                              {!isCleaningWindowSaved ? (
                                <SaveFirstBanner
                                  title="Configure Cleaning Window First"
                                  description="Please save your Cleaning Window configuration before adding devices. This sets the available time budget."
                                />
                              ) : hasMultipleWindows ? (
                                <>
                                  <div className="space-y-3">
                                    {windows
                                      .filter((w) => w.days.length > 0)
                                      .map((window, winIndex) => {
                                        const windowProcesses = window.cleaningProcesses || [];
                                        const isExpanded = expandedWindowIds.includes(window.id);
                                        const windowKey = window?.id ?? `window-${winIndex}`;

                                        const windowTotalMinutes = calculateMinutes(
                                          window.sessions[0]?.primaryRange?.from || '08:00',
                                          window.sessions[0]?.primaryRange?.to || '16:00',
                                        );

                                        const windowTimeUsed = windowProcesses.reduce(
                                          (sum, p) => sum + p.timeStandard.maximum,
                                          0,
                                        );
                                        const windowTimeRemaining =
                                          windowTotalMinutes - windowTimeUsed;

                                        const deviceCount = windowProcesses.length;
                                        const hasDevices = deviceCount > 0;
                                        const statusColor = hasDevices
                                          ? 'text-green-600'
                                          : 'text-red-600';
                                        const statusBgColor = hasDevices
                                          ? 'bg-green-100'
                                          : 'bg-red-100';
                                        const statusText = hasDevices
                                          ? (
                                            <>
                                              <Check className="w-3.5 h-3.5 inline shrink-0 align-middle" aria-hidden />
                                              {' '}{deviceCount} device{deviceCount > 1 ? 's' : ''}
                                            </>
                                          )
                                          : (
                                            <>
                                              <AlertTriangle className="w-3.5 h-3.5 inline shrink-0 align-middle" aria-hidden />
                                              {' '}Not configured
                                            </>
                                          );

                                        const daysSummary = window.days
                                          .map((d) => d.substring(0, 3))
                                          .join(', ');
                                        const timeSummary = window.sessions[0]?.primaryRange
                                          ? `${window.sessions[0].primaryRange.from}-${window.sessions[0].primaryRange.to}`
                                          : '';

                                        return (
                                          <div
                                            key={windowKey}
                                            className="border border-gray-200 rounded-lg overflow-hidden bg-white"
                                          >
                                            <button
                                              type="button"
                                              onClick={() => toggleWindowAccordion(window.id)}
                                              className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
                                            >
                                              <div className="flex items-center gap-3 flex-1">
                                                <div className="flex-1 text-left">
                                                  <h5 className="font-['Roboto:SemiBold',sans-serif] text-[15px] text-black">
                                                    {window.name}
                                                  </h5>
                                                  <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-600 mt-0.5">
                                                    {daysSummary} • {timeSummary}
                                                  </p>
                                                </div>
                                                <span
                                                  className={`px-2.5 py-1 rounded-full font-['Roboto:Medium',sans-serif] text-[12px] ${statusColor} ${statusBgColor}`}
                                                >
                                                  {statusText}
                                                </span>
                                              </div>
                                              {isExpanded ? (
                                                <ChevronDown className="w-5 h-5 text-gray-500 ml-2" />
                                              ) : (
                                                <ChevronRight className="w-5 h-5 text-gray-500 ml-2" />
                                              )}
                                            </button>

                                            <AnimatePresence>
                                              {isExpanded && (
                                                <motion.div
                                                  initial={{ height: 0, opacity: 0 }}
                                                  animate={{ height: 'auto', opacity: 1 }}
                                                  exit={{ height: 0, opacity: 0 }}
                                                  transition={{ duration: 0.2 }}
                                                  className="overflow-hidden"
                                                >
                                                  <div className="px-4 pb-4 pt-2 space-y-4 border-t border-gray-200">
                                                    {/* Time Budget - same style as Window Time Distribution (image 2) */}
                                                    <div className="rounded-2xl border border-[#A5D8FF] bg-[#E8F4FC] p-4 shadow-sm flex items-center gap-3">
                                                      <Clock
                                                        className="w-5 h-5 flex-shrink-0 text-[#61b8e7]"
                                                        strokeWidth={2}
                                                      />
                                                      <div className="flex-1">
                                                        <p className="font-['Roboto:SemiBold',sans-serif] text-[13px] text-[#333C4D]">
                                                          Time Budget: {windowTimeUsed}/
                                                          {windowTotalMinutes} min used •{' '}
                                                          {windowTimeRemaining} min remaining
                                                        </p>
                                                      </div>
                                                    </div>

                                                    {/* Device List */}
                                                    {windowProcesses.length > 0 && (
                                                      <div className="space-y-2">
                                                        {windowProcesses.map((process, procIndex) => {
                                                          const deviceNames = {
                                                            MS50: 'MS-50',
                                                            V50: 'V-50',
                                                            A50: 'A-50',
                                                          };
                                                          const isEditingThis =
                                                            editingProcessId === process.id;
                                                          const processKey = process?.id ?? `process-${window.id}-${procIndex}`;

                                                          return (
                                                            <div
                                                              key={processKey}
                                                              className={`rounded-lg p-3 border ${
                                                                isEditingThis
                                                                  ? 'bg-[#F0F9FF] border-[#B0D7F0]'
                                                                  : 'bg-gray-50 border-gray-200'
                                                              }`}
                                                            >
                                                              <div className="flex items-center justify-between">
                                                                <div className="flex-1">
                                                                  <h6 className="font-['Roboto:SemiBold',sans-serif] text-[14px] text-black">
                                                                    {
                                                                      deviceNames[
                                                                        process.assignedDevice
                                                                      ]
                                                                    }
                                                                  </h6>
                                                                  <p className="font-['Roboto:Regular',sans-serif] text-[12px] text-gray-600 mt-0.5">
                                                                    Time:{' '}
                                                                    {process.timeStandard.minimum}-
                                                                    {process.timeStandard.maximum}{' '}
                                                                    min
                                                                  </p>
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                  <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                      setSelectedWindowId(window.id);
                                                                      handleStartEditingProcess(
                                                                        process,
                                                                        window.id,
                                                                      );
                                                                    }}
                                                                    className={`p-2 rounded-lg transition-colors group ${
                                                                      isEditingThis
                                                                        ? 'bg-[#B0D7F0]/30'
                                                                        : 'hover:bg-gray-100'
                                                                    }`}
                                                                    aria-label="Edit device"
                                                                    aria-pressed={isEditingThis}
                                                                  >
                                                                    <Pencil
                                                                      className={`w-4 h-4 ${
                                                                        isEditingThis
                                                                          ? 'text-[#61b8e7]'
                                                                          : 'text-gray-400 group-hover:text-gray-600'
                                                                      }`}
                                                                    />
                                                                  </button>
                                                                  <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                      setSelectedWindowId(window.id);
                                                                      handleDeleteProcess(process.id);
                                                                    }}
                                                                    className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                                                                    aria-label="Delete device"
                                                                  >
                                                                    <Trash2 className="w-4 h-4 text-gray-400 group-hover:text-red-600" />
                                                                  </button>
                                                                </div>
                                                              </div>
                                                            </div>
                                                          );
                                                        })}
                                                      </div>
                                                    )}

                                                    {/* Inline Add Device Form */}
                                                    <AnimatePresence>
                                                      {addingDeviceToWindowId === window.id && (
                                                        <motion.div
                                                          initial={{ opacity: 0, scale: 0.95 }}
                                                          animate={{ opacity: 1, scale: 1 }}
                                                          exit={{ opacity: 0, scale: 0.95 }}
                                                          transition={{ duration: 0.2 }}
                                                        >
                                                          <div className="bg-white border border-[#B0D7F0] rounded-xl p-4 space-y-4 shadow-sm">
                                                            {editingProcessId && (
                                                              <h5 className="font-['Roboto:SemiBold',sans-serif] text-[15px] text-black">
                                                                Edit Device
                                                              </h5>
                                                            )}
                                                            <div>
                                                              <label className="block font-['Roboto:Medium',sans-serif] text-[13px] text-gray-700 mb-2">
                                                                Select Device{' '}
                                                                <span className="text-red-500">
                                                                  *
                                                                </span>
                                                              </label>
                                                              <select
                                                                value={newDeviceType}
                                                                onChange={(e) =>
                                                                  setNewDeviceType(e.target.value)
                                                                }
                                                                className="w-full min-h-[42px] pl-4 pr-10 py-2.5 border border-[#D3D3D3] rounded-xl font-['Roboto:Regular',sans-serif] text-[14px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#61b8e7] focus:border-[#61b8e7] bg-white appearance-none bg-no-repeat bg-[length:14px_14px]"
                                                                style={{
                                                                  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%234a5568' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
                                                                  backgroundPosition: 'right 12px center',
                                                                }}
                                                              >
                                                                <option value="">
                                                                  Choose a device...
                                                                </option>
                                                                <option
                                                                  value="MS50"
                                                                  disabled={alreadyAddedDevicesForSelect.includes('MS50')}
                                                                >
                                                                  MS-50{' '}
                                                                  {alreadyAddedDevicesForSelect.includes('MS50')
                                                                    ? '(Already added)'
                                                                    : ''}
                                                                </option>
                                                                <option
                                                                  value="V50"
                                                                  disabled={alreadyAddedDevicesForSelect.includes('V50')}
                                                                >
                                                                  V-50{' '}
                                                                  {alreadyAddedDevicesForSelect.includes('V50')
                                                                    ? '(Already added)'
                                                                    : ''}
                                                                </option>
                                                                <option
                                                                  value="A50"
                                                                  disabled={alreadyAddedDevicesForSelect.includes('A50')}
                                                                >
                                                                  A-50{' '}
                                                                  {alreadyAddedDevicesForSelect.includes('A50')
                                                                    ? '(Already added)'
                                                                    : ''}
                                                                </option>
                                                              </select>
                                                            </div>

                                                            <div className="grid grid-cols-2 gap-3">
                                                              <TimeStandardControl
                                                                label={
                                                                  newDeviceType === 'MS50'
                                                                    ? 'Min Mopping Duration (min)'
                                                                    : 'Minimum (min)'
                                                                }
                                                                value={newDeviceTimeMin}
                                                                onChange={handleTimeMinChange}
                                                              />
                                                              <TimeStandardControl
                                                                label={
                                                                  newDeviceType === 'MS50'
                                                                    ? 'Max Mopping Duration (min)'
                                                                    : 'Maximum (min)'
                                                                }
                                                                value={newDeviceTimeMax}
                                                                onChange={handleTimeMaxChange}
                                                              />
                                                            </div>

                                                            {timeValidationError && (
                                                              <div className="text-red-600 text-[13px] font-['Roboto:Regular',sans-serif] flex items-start gap-1.5">
                                                                <XCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
                                                                <span>{timeValidationError}</span>
                                                              </div>
                                                            )}

                                                            {newDeviceType === 'MS50' && (
                                                              <TimeStandardControl
                                                                label="Rinse & Wring Cycles *"
                                                                value={ms50RinseWringCycles}
                                                                onChange={setMs50RinseWringCycles}
                                                              />
                                                            )}

                                                            <div className="flex gap-2 pt-2">
                                                              <button
                                                                type="button"
                                                                onClick={handleCancelAddingDevice}
                                                                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl font-['Roboto:Medium',sans-serif] text-[14px] hover:bg-gray-50 transition-colors bg-white"
                                                              >
                                                                Cancel
                                                              </button>
                                                              <button
                                                                type="button"
                                                                onClick={handleSaveNewDevice}
                                                                disabled={
                                                                  !newDeviceType ||
                                                                  !newDeviceTimeMin ||
                                                                  !newDeviceTimeMax ||
                                                                  !!timeValidationError ||
                                                                  (newDeviceType === 'MS50' &&
                                                                    !ms50RinseWringCycles)
                                                                }
                                                                className="flex-1 px-4 py-2.5 bg-[#87CEEB] text-white rounded-xl font-['Roboto:Medium',sans-serif] text-[14px] hover:bg-[#7EB8E8] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                              >
                                                                {editingProcessId ? 'Update Device' : 'Add Device'}
                                                              </button>
                                                            </div>
                                                          </div>
                                                        </motion.div>
                                                      )}
                                                    </AnimatePresence>

                                                    {addingDeviceToWindowId !== window.id && (
                                                      <AddPlaceholder
                                                        label="Add Device"
                                                        onClick={() => {
                                                          setSelectedWindowId(window.id);
                                                          handleStartAddingDevice(window.id);
                                                        }}
                                                        disabled={
                                                          windowProcesses.length >= 3 ||
                                                          windowTimeRemaining <= 0
                                                        }
                                                      />
                                                    )}
                                                  </div>
                                                </motion.div>
                                              )}
                                            </AnimatePresence>
                                          </div>
                                        );
                                      })}
                                  </div>
                                </>
                              ) : (
                                <>
                                  {/* Single Window Flow - same style as Window Time Distribution (image 2) */}
                                  <div className="rounded-2xl border border-[#A5D8FF] bg-[#E8F4FC] p-4 shadow-sm flex items-center gap-3">
                                    <Clock
                                      className="w-5 h-5 flex-shrink-0 text-[#61b8e7]"
                                      strokeWidth={2}
                                    />
                                    <div className="flex-1">
                                      <p className="font-['Roboto:SemiBold',sans-serif] text-[13px] text-[#333C4D]">
                                        Time Budget: {totalTimeUsed}/{totalMinutes} min used •{' '}
                                        {timeRemaining} min remaining
                                      </p>
                                    </div>
                                  </div>

                                  {currentProcesses.length > 0 && (
                                    <div className="space-y-3">
                                      <h5 className="font-['Roboto:SemiBold',sans-serif] text-[14px] text-gray-900">
                                        Configured Devices
                                      </h5>
                                      {currentProcesses.map((process, procIndex) => {
                                        const deviceNames = {
                                          MS50: 'MS-50',
                                          V50: 'V-50',
                                          A50: 'A-50',
                                        };

                                        const isEditingThis = editingProcessId === process.id;
                                        const processKey = process?.id ?? `process-${procIndex}`;
                                        return (
                                          <div
                                            key={processKey}
                                            className={`rounded-lg p-4 border ${
                                              isEditingThis
                                                ? 'bg-[#F0F9FF] border-[#B0D7F0]'
                                                : 'bg-white border-gray-200'
                                            }`}
                                          >
                                            <div className="flex items-center justify-between">
                                              <div className="flex-1">
                                                <h5 className="font-['Roboto:SemiBold',sans-serif] text-[15px] text-black">
                                                  {deviceNames[process.assignedDevice]}
                                                </h5>
                                                <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-600 mt-0.5">
                                                  Time standard: {process.timeStandard.minimum}-
                                                  {process.timeStandard.maximum} min
                                                </p>
                                              </div>
                                              <div className="flex items-center gap-1">
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    handleStartEditingProcess(
                                                      process,
                                                      selectedWindowId,
                                                    )
                                                  }
                                                  className={`p-2 rounded-lg transition-colors group ${
                                                    isEditingThis
                                                      ? 'bg-[#B0D7F0]/30 text-[#61b8e7]'
                                                      : 'hover:bg-gray-100'
                                                  }`}
                                                  aria-label="Edit device"
                                                  aria-pressed={isEditingThis}
                                                >
                                                  <Pencil
                                                    className={`w-4 h-4 ${
                                                      isEditingThis
                                                        ? 'text-[#61b8e7]'
                                                        : 'text-gray-400 group-hover:text-gray-600'
                                                    }`}
                                                  />
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => handleDeleteProcess(process.id)}
                                                  className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                                                  aria-label="Delete device"
                                                >
                                                  <Trash2 className="w-4 h-4 text-gray-400 group-hover:text-red-600" />
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}

                                  {!isAddingDevice ? (
                                    <div>
                                      <AddPlaceholder
                                        label="Add Device"
                                        onClick={() => handleStartAddingDevice()}
                                        disabled={!canAddMoreDevices}
                                      />
                                      {!canAddMoreDevices && (
                                        <p className="text-[12px] text-gray-500 font-['Roboto:Regular',sans-serif] text-center mt-2">
                                          {alreadyAddedDevices.length >= 3
                                            ? 'Maximum 3 devices reached'
                                            : 'Not enough time remaining in cleaning window'}
                                        </p>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="bg-white border border-[#B0D7F0] rounded-xl p-4 space-y-4 shadow-sm">
                                      {editingProcessId && (
                                        <h5 className="font-['Roboto:SemiBold',sans-serif] text-[15px] text-black">
                                          Edit Device
                                        </h5>
                                      )}
                                      <div>
                                        <label className="block font-['Roboto:Medium',sans-serif] text-[13px] text-gray-700 mb-2">
                                          Select Device{' '}
                                          <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                          value={newDeviceType}
                                          onChange={(e) => setNewDeviceType(e.target.value)}
                                          className="w-full min-h-[42px] pl-4 pr-10 py-2.5 border border-[#D3D3D3] rounded-xl font-['Roboto:Regular',sans-serif] text-[14px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#61b8e7] focus:border-[#61b8e7] bg-white appearance-none bg-no-repeat bg-[length:14px_14px]"
                                          style={{
                                            backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%234a5568' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
                                            backgroundPosition: 'right 12px center',
                                          }}
                                        >
                                          <option value="">Choose a device...</option>
                                          <option
                                            value="MS50"
                                            disabled={alreadyAddedDevicesForSelect.includes('MS50')}
                                          >
                                            MS-50{' '}
                                            {alreadyAddedDevicesForSelect.includes('MS50')
                                              ? '(Already added)'
                                              : ''}
                                          </option>
                                          <option
                                            value="V50"
                                            disabled={alreadyAddedDevicesForSelect.includes('V50')}
                                          >
                                            V-50{' '}
                                            {alreadyAddedDevicesForSelect.includes('V50')
                                              ? '(Already added)'
                                              : ''}
                                          </option>
                                          <option
                                            value="A50"
                                            disabled={alreadyAddedDevicesForSelect.includes('A50')}
                                          >
                                            A-50{' '}
                                            {alreadyAddedDevicesForSelect.includes('A50')
                                              ? '(Already added)'
                                              : ''}
                                          </option>
                                        </select>
                                      </div>

                                      <div className="grid grid-cols-2 gap-3">
                                        <TimeStandardControl
                                          label={
                                            newDeviceType === 'MS50'
                                              ? 'Min Mopping Duration (min)'
                                              : 'Minimum (min)'
                                          }
                                          value={newDeviceTimeMin}
                                          onChange={handleTimeMinChange}
                                        />
                                        <TimeStandardControl
                                          label={
                                            newDeviceType === 'MS50'
                                              ? 'Max Mopping Duration (min)'
                                              : 'Maximum (min)'
                                          }
                                          value={newDeviceTimeMax}
                                          onChange={handleTimeMaxChange}
                                        />
                                      </div>

                                      {timeValidationError && (
                                        <div className="text-red-600 text-[13px] font-['Roboto:Regular',sans-serif] flex items-start gap-1.5">
                                          <XCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
                                          <span>{timeValidationError}</span>
                                        </div>
                                      )}

                                      {newDeviceType === 'MS50' && (
                                        <TimeStandardControl
                                          label="Rinse & Wring Cycles *"
                                          value={ms50RinseWringCycles}
                                          onChange={setMs50RinseWringCycles}
                                        />
                                      )}

                                      <div className="flex gap-2 pt-2">
                                        <button
                                          type="button"
                                          onClick={handleCancelAddingDevice}
                                          className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl font-['Roboto:Medium',sans-serif] text-[14px] hover:bg-gray-50 transition-colors bg-white"
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          type="button"
                                          onClick={handleSaveNewDevice}
                                          disabled={
                                            !newDeviceType ||
                                            !newDeviceTimeMin ||
                                            !newDeviceTimeMax ||
                                            !!timeValidationError ||
                                            (newDeviceType === 'MS50' && !ms50RinseWringCycles)
                                          }
                                          className="flex-1 px-4 py-2.5 bg-[#87CEEB] text-white rounded-xl font-['Roboto:Medium',sans-serif] text-[14px] hover:bg-[#7EB8E8] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                          {editingProcessId ? 'Update Device' : 'Add Device'}
                                        </button>
                                      </div>
                                    </div>
                                  )}

                                  {currentProcesses.length > 0 && validation && (
                                    <div className={`${INFO_CARD_WRAPPER_P5} space-y-3`}>
                                      {validation.details && (
                                        <div className={`space-y-2 ${INFO_CARD_TEXT}`}>
                                          <div className="flex justify-between items-center">
                                            <span>Total Window Time:</span>
                                            <span className="font-medium text-[#333C4D]">
                                              {validation.details.available} min
                                            </span>
                                          </div>

                                          <div className="flex justify-between items-center">
                                            <span>
                                              <Bot className="w-4 h-4 inline shrink-0 align-middle mr-1" aria-hidden />
                                              Devices Time:
                                            </span>
                                            <span className="font-medium text-[#333C4D]">
                                              {validation.details.devicesMin}-
                                              {validation.details.devicesMax} min
                                            </span>
                                          </div>

                                          {validation.details.otherTasksMax > 0 && (
                                            <div className="flex justify-between items-center">
                                              <span>
                                                <Sparkles className="w-4 h-4 inline shrink-0 align-middle mr-1" aria-hidden />
                                                Other Tasks Time:
                                              </span>
                                              <span className="font-medium text-[#333C4D]">
                                                {validation.details.otherTasksMin}-
                                                {validation.details.otherTasksMax} min
                                              </span>
                                            </div>
                                          )}

                                          <div className="flex justify-between items-center pt-2 border-t border-[#A5D8FF]">
                                            <span className="font-medium text-[#333C4D]">
                                              <Clock className="w-4 h-4 inline shrink-0 align-middle mr-1 text-[#61b8e7]" aria-hidden />
                                              Time left:
                                            </span>
                                            <span className="font-semibold text-[#333C4D]">
                                              {validation.details.margin} min
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </>
                              )}

                              {isCleaningWindowSaved && (
                                <div className="flex justify-between items-center pt-4 border-t border-gray-300">
                                  <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-600">
                                    {hasMultipleWindows
                                      ? windows
                                          .filter((w) => w.days.length > 0)
                                          .map((w, idx) => {
                                            const count = (w.cleaningProcesses || []).length;
                                            const StatusIcon = count > 0 ? Check : AlertTriangle;
                                            return {
                                              id: w?.id ?? `window-summary-${idx}`,
                                              icon: StatusIcon,
                                              name: w.name,
                                              count,
                                            };
                                          })
                                          .map((item, i) => {
                                            const StatusIcon = item.icon;
                                            return (
                                              <React.Fragment key={item.id}>
                                                {i > 0 && ' • '}
                                                <StatusIcon className="w-3.5 h-3.5 inline shrink-0 align-middle" aria-hidden />
                                                {' '}{item.name}: {item.count}
                                              </React.Fragment>
                                            );
                                          })
                                      : currentProcesses.length > 0
                                        ? `${currentProcesses.length} device(s) configured`
                                        : 'Add at least one device'}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() => handleSaveSection('cleaning-processes')}
                                    disabled={
                                      !allWindowsHaveDevices || validation?.status === 'impossible'
                                    }
                                    className="px-6 py-2.5 bg-[#61b8e7] text-white rounded-lg font-['Roboto:Medium',sans-serif] text-[14px] hover:bg-[#4fa3d1] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    Save & Continue
                                  </button>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* 3. Other Tasks */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleSection('other-tasks')}
                        className="w-full bg-white hover:bg-gray-50 px-5 py-4 flex items-center justify-between transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {renderStatusIcon('other-tasks')}
                          <h3 className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[16px] text-black">
                            Other Cleaning Tasks{' '}
                            <span className="ml-2 text-[13px] text-gray-500 font-normal">
                              (Optional)
                            </span>
                          </h3>
                        </div>
                        {expandedSections.includes('other-tasks') ? (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-500" />
                        )}
                      </button>

                      <AnimatePresence>
                        {expandedSections.includes('other-tasks') && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="bg-gray-50 px-5 py-5 space-y-5">
                              <div className="space-y-2">
                                <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-700 leading-relaxed">
                                  Define the time range for manual cleaning tasks (trash removal,
                                  restocking supplies, etc.). This time is included within the
                                  cleaning window.
                                </p>
                                <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-500">
                                  The system tracks when staff are present in an area without
                                  device activity.
                                </p>
                              </div>

                              {!isCleaningWindowSaved ? (
                                <SaveFirstBanner
                                  title="Save Cleaning Window First"
                                  description="Configure and save your cleaning window to see the time distribution and allocate time for manual tasks effectively."
                                />
                              ) : (
                                <div className={`${INFO_CARD_WRAPPER_P5} space-y-3`}>
                                  <h4 className={INFO_CARD_TITLE}>
                                    Window Time Distribution
                                  </h4>
                                  <div className={`space-y-2 ${INFO_CARD_TEXT}`}>
                                    <div className="flex justify-between items-center">
                                      <span>Total Window Time:</span>
                                      <span className="font-medium text-[#333C4D]">
                                        {totalMinutes} min
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <span className="inline-flex items-center gap-1">
                                        <Bot className="w-4 h-4 shrink-0" aria-hidden />
                                        Devices Time:
                                      </span>
                                      <span className="font-medium text-[#333C4D]">
                                        {validation?.details?.devicesMin || 0}-
                                        {validation?.details?.devicesMax || 0} min
                                      </span>
                                    </div>
                                    {otherTasksMinDuration && otherTasksMaxDuration && (
                                      <div className="flex justify-between items-center">
                                        <span className="inline-flex items-center gap-1">
                                          <Sparkles className="w-4 h-4 shrink-0" aria-hidden />
                                          Other Tasks Time:
                                        </span>
                                        <span className="font-medium text-[#333C4D]">
                                          {otherTasksMinDuration}-{otherTasksMaxDuration} min
                                        </span>
                                      </div>
                                    )}
                                    <div className="pt-2 border-t border-[#A5D8FF] flex justify-between items-center">
                                      <span className="font-medium inline-flex items-center gap-1">
                                        <Clock className="w-4 h-4 shrink-0 text-[#61b8e7]" aria-hidden />
                                        Time left:
                                      </span>
                                      <span className="font-semibold text-[#333C4D]">
                                        {timeRemaining} min
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}

                              <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                  <TimeStandardControl
                                    label="Minimum Duration (min)"
                                    value={otherTasksMinDuration}
                                    onChange={handleOtherTasksMinChange}
                                  />
                                  <TimeStandardControl
                                    label="Maximum Duration (min)"
                                    value={otherTasksMaxDuration}
                                    onChange={handleOtherTasksMaxChange}
                                  />
                                </div>

                                {otherTasksTimeError && (
                                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                                    <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-red-700">
                                      {otherTasksTimeError}
                                    </p>
                                  </div>
                                )}
                              </div>

                              <div className="flex justify-between items-center pt-4 border-t border-gray-300">
                                <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-600">
                                  {otherTasksMinDuration && otherTasksMaxDuration
                                    ? `${otherTasksMinDuration}-${otherTasksMaxDuration} min configured`
                                    : 'Optional: Configure manual tasks time'}
                                </p>
                                <button
                                  type="button"
                                  onClick={() => handleSaveSection('other-tasks')}
                                  className="px-6 py-2.5 bg-[#61b8e7] text-white rounded-lg font-['Roboto:Medium',sans-serif] text-[14px] hover:bg-[#4fa3d1] transition-colors"
                                >
                                  {otherTasksMinDuration && otherTasksMaxDuration
                                    ? 'Save & Continue'
                                    : 'Skip for Now'}
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>

                {/* Footer: Cancel + single action button (Save progress when partial, Save standards when complete) */}
                <div className="border-t border-gray-200 px-8 py-4 bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      {savedSectionsCount === 0 && (
                        <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-500">
                          Save at least one section to continue
                        </p>
                      )}
                      {savedSectionsCount > 0 && !canSave && (
                        <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-500">
                          Complete Cleaning Window and Processes to save final standards
                        </p>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={canSave ? handleFinalSave : handleSaveProgress}
                        disabled={savedSectionsCount === 0}
                        className="px-6 py-2.5 font-['Roboto:Medium',sans-serif] text-[14px] text-white bg-[#61b8e7] rounded-lg hover:bg-[#4da3d1] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        type="button"
                      >
                        {canSave ? 'Save standards' : 'Save progress'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
